import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { adminApi, ManagedUser, Role, ROLES, roleRank, parseClientNames } from 'util/adminApi';
import { SUGGEST } from 'components/FieldSuggestions';
import { ui } from 'util/ui';
import { CrossIcon } from 'components/shell/Icons';
import { Panel, SectionHead, TRow, Cell, AdminButton, Pill, Notice } from './adminUi';

/**
 * Admin › Users, backed by the Phase 1 server functions: real sign-in status and controls.
 * UI refresh styling (5 Nov 2026): shared Admin panels, buttons and pills; on a phone each user is a
 * stacked card. Behaviour unchanged.
 */

const SmallButton = AdminButton;

const inputStyle: React.CSSProperties = {
  height: 36,
  boxSizing: 'border-box',
  border: `1px solid ${ui.control}`,
  borderRadius: 9,
  padding: '0 10px',
  fontFamily: 'inherit',
  fontSize: 14,
  color: ui.ink,
  background: ui.surface,
};

const selectStyle: React.CSSProperties = { ...inputStyle, height: 32, padding: '0 8px', fontSize: 13, maxWidth: '100%' };

/** Choose the client/agency names a client login can see (task #1). Suggests existing spellings. */
const ClientPicker: FC<{ names: string[]; onChange(names: string[]): void }> = ({ names, onChange }) => {
  const [text, setText] = useState('');
  const add = () => {
    const next = parseClientNames([...names, text].join(','));
    onChange(next);
    setText('');
  };
  return (
    <div style={{ marginTop: 12 }}>
      <p style={{ margin: '0 0 8px', fontSize: 13, color: ui.muted }}>
        Client or agency names this login can see. Projects match if their Client or Agency is any of these. Add every spelling in
        use, e.g. One3six, One3six Creative and One3Six Agency.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
        {names.map((n) => (
          <span key={n} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 28, padding: '0 6px 0 10px', borderRadius: 6, background: ui.subtle, fontSize: 12, color: ui.text2 }}>
            {n}
            <button
              type="button"
              aria-label={`Remove ${n}`}
              onClick={() => onChange(names.filter((x) => x !== n))}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 2, display: 'flex' }}
            >
              <CrossIcon size={11} color={ui.muted} width={2.6} />
            </button>
          </span>
        ))}
        <input
          placeholder="Start typing a client or agency…"
          value={text}
          list={SUGGEST.org}
          autoComplete="off"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          style={{ ...inputStyle, flex: 1, minWidth: 220 }}
        />
        <SmallButton onClick={add} disabled={!text.trim()}>
          Add
        </SmallButton>
      </div>
    </div>
  );
};

const previewHref = (names: string[], masters: boolean) =>
  `/portal?as=${encodeURIComponent(names.join('|'))}${masters ? '&masters=1' : ''}`;

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Never');

type Filter = 'all' | 'active' | 'disabled';

const LinkBox: FC<{ title: string; link: string; onClose(): void }> = ({ title, link, onClose }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Panel padding="14px 16px" style={{ marginBottom: 14, borderColor: '#A9DDBF', background: '#F3FBF6' }}>
      <div style={{ fontSize: 15, fontWeight: 700 }}>{title}</div>
      <p style={{ margin: '4px 0 10px', fontSize: 13, color: ui.muted }}>
        Send this link to them yourself. It lets them choose a password, works once, and expires in about an hour.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <input readOnly value={link} onFocus={(e) => e.target.select()} style={{ ...inputStyle, flex: 1, minWidth: 220, fontSize: 12 }} />
        <SmallButton
          tone="primary"
          onClick={() => {
            navigator.clipboard.writeText(link).then(() => setCopied(true));
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </SmallButton>
        <SmallButton onClick={onClose}>Done</SmallButton>
      </div>
    </Panel>
  );
};

const COLS = 'minmax(0,3fr) minmax(0,1.5fr) 0.9fr 1.1fr minmax(0,2.2fr)';

const UsersManager: FC<{ currentUid?: string; currentRole: Role | null }> = ({ currentUid, currentRole }) => {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [orphans, setOrphans] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [link, setLink] = useState<{ title: string; link: string } | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<Role>('member');
  const [newClients, setNewClients] = useState<string[]>([]);
  const [newMasters, setNewMasters] = useState(false);

  const isOwner = currentRole === 'owner';
  const myRank = roleRank(currentRole);
  const canManage = (u: ManagedUser) => u.uid !== currentUid && (isOwner || roleRank(u.role) < myRank);
  const grantable = ROLES.filter((r) => isOwner || roleRank(r.value) < myRank);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.listUsers();
      setUsers(res.users.sort((a, b) => (a.email || '').localeCompare(b.email || '')));
      setOrphans(res.orphanProfiles.length);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (key: string, action: () => Promise<unknown>) => {
    setBusy(key);
    setError(null);
    try {
      await action();
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const toggleDisabled = (u: ManagedUser) => {
    const verb = u.disabled ? 'Turn sign-in back on for' : 'Turn off sign-in for';
    if (!window.confirm(`${verb} ${u.email}?${u.disabled ? '' : ' They will be signed out.'}`)) return;
    run(u.uid, () => adminApi.setDisabled(u.uid, !u.disabled));
  };

  /** Ask which names a client login sees, and whether it can download masters. null = cancelled. */
  const askClientAccess = (u: ManagedUser) => {
    const typed = window.prompt(
      `Which client or agency names can ${u.email} see?\nSeparate several with commas, e.g. One3six, One3six Creative`,
      (u.clients || []).join(', '),
    );
    if (typed === null) return null;
    const clients = parseClientNames(typed);
    if (!clients.length) {
      setError('A client login needs at least one client or agency name.');
      return null;
    }
    const clientMasters = window.confirm(
      `Can ${u.email} download master files too?\n\nOK = yes, masters as well.\nCancel = previews and other files only (masters show as "on request").`,
    );
    return { clients, clientMasters };
  };

  const changeRole = (u: ManagedUser, value: string) => {
    const role = value === '' ? null : (value as Role);
    const label = role ? ROLES.find((r) => r.value === role)!.label : 'no access';
    if (role === 'client') {
      const access = askClientAccess(u);
      if (!access) return;
      if (!window.confirm(`Make ${u.email} a client login for ${access.clients.join(', ')}? They will be signed out.`)) return;
      run(u.uid, () => adminApi.setRole(u.uid, role, access));
      return;
    }
    if (!window.confirm(`Change ${u.email} to ${label}?`)) return;
    run(u.uid, () => adminApi.setRole(u.uid, role));
  };

  const editClientAccess = (u: ManagedUser) => {
    const access = askClientAccess(u);
    if (!access) return;
    run(u.uid, () => adminApi.setRole(u.uid, 'client', access));
  };

  const deleteUser = (u: ManagedUser) => {
    const typed = window.prompt(
      `Delete ${u.email} for good?\n\nThis removes their sign-in. It can't be undone, though they can be invited again later. ` +
        `Projects and files they added are not touched.\n\nType their email to confirm:`,
    );
    if (typed === null) return;
    run(u.uid, () => adminApi.deleteUser(u.uid, typed));
  };

  const resetLink = (u: ManagedUser) =>
    run(u.uid, async () => {
      const res = await adminApi.resetLink(u.uid);
      setLink({ title: `Password reset link for ${res.email}`, link: res.link });
    });

  const create = () =>
    run('create', async () => {
      const res = await adminApi.createUser(
        newEmail.trim(),
        newRole,
        newRole === 'client' ? { clients: newClients, clientMasters: newMasters } : undefined,
      );
      setNewEmail('');
      setNewClients([]);
      setNewMasters(false);
      setLink({ title: `Account created for ${res.email}`, link: res.setupLink });
    });

  const counts = useMemo(
    () => ({
      active: users.filter((u) => !u.disabled).length,
      disabled: users.filter((u) => u.disabled).length,
      noRole: users.filter((u) => !u.disabled && !u.role).length,
    }),
    [users],
  );
  const shown = users.filter((u) => filter === 'all' || (filter === 'active' ? !u.disabled : u.disabled));

  return (
    <section style={{ marginBottom: 28, fontFamily: ui.font }}>
      <SectionHead
        title="Users"
        note={`${counts.active} active · ${counts.disabled} disabled${counts.noRole > 0 ? ` · ${counts.noRole} active with no role (can't see anything)` : ''}`}
      >
        {/* Three equal-width options in one control (Aaron, 5 Nov 2026). */}
        <div
          role="group"
          aria-label="Show users"
          style={{ display: 'inline-grid', gridTemplateColumns: 'repeat(3, 88px)', gap: 4, padding: 4, background: ui.subtle, borderRadius: 12 }}
        >
          {(['all', 'active', 'disabled'] as Filter[]).map((f) => {
            const on = filter === f;
            return (
              <button
                key={f}
                type="button"
                aria-pressed={on}
                onClick={() => setFilter(f)}
                style={{
                  height: 32,
                  border: 'none',
                  borderRadius: 9,
                  background: on ? ui.surface : 'transparent',
                  boxShadow: on ? '0 1px 3px rgba(0,0,0,0.14)' : 'none',
                  color: on ? ui.ink : ui.muted,
                  fontFamily: ui.font,
                  fontSize: 13,
                  fontWeight: on ? 600 : 500,
                  cursor: 'pointer',
                }}
              >
                {f === 'all' ? 'All' : f === 'active' ? 'Active' : 'Disabled'}
              </button>
            );
          })}
        </div>
      </SectionHead>

      {error && <Notice tone="error">{error}</Notice>}
      {link && <LinkBox title={link.title} link={link.link} onClose={() => setLink(null)} />}

      <Panel padding="16px 18px" style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>Add a user</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <input
            placeholder="name@company.com"
            aria-label="Email of the new user"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            style={{ ...inputStyle, flex: 1, minWidth: 220 }}
          />
          <select aria-label="Role" style={{ ...selectStyle, height: 36 }} value={newRole} onChange={(e) => setNewRole(e.target.value as Role)}>
            {grantable.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <SmallButton
            tone="primary"
            onClick={create}
            disabled={!newEmail.includes('@') || busy === 'create' || (newRole === 'client' && newClients.length === 0)}
          >
            {busy === 'create' ? 'Creating…' : 'Create account'}
          </SmallButton>
        </div>
        <p style={{ margin: '10px 0 0', fontSize: 13, color: ui.muted }}>
          {ROLES.find((r) => r.value === newRole)?.help}. No password is set; you get a link to send them.
        </p>
        {newRole === 'client' && (
          <>
            <ClientPicker names={newClients} onChange={setNewClients} />
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginTop: 10 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                <input type="checkbox" checked={newMasters} onChange={(e) => setNewMasters(e.target.checked)} />
                Can download master files (otherwise masters show as "on request")
              </label>
              {newClients.length > 0 && (
                <a href={previewHref(newClients, newMasters)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 14 }}>
                  Preview what they'll see →
                </a>
              )}
            </div>
          </>
        )}
      </Panel>

      <Panel>
        <TRow head columns={COLS}>
          <Cell>Email</Cell>
          <Cell>Role</Cell>
          <Cell>Status</Cell>
          <Cell>Last sign-in</Cell>
          <Cell>Actions</Cell>
        </TRow>
        {loading && !users.length && <p style={{ margin: 0, padding: '14px 16px', fontSize: 14, color: ui.muted }}>Loading accounts…</p>}
        {shown.map((u) => {
          const manageable = canManage(u);
          return (
            <TRow key={u.uid} columns={COLS} style={{ opacity: busy === u.uid ? 0.5 : 1 }}>
              <Cell style={{ overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500 }}>
                {u.email || '(no email)'}
                {u.uid === currentUid && <span style={{ color: ui.muted, fontSize: 12, fontWeight: 400 }}> (you)</span>}
                {u.legacyAdmin && !u.role && <span style={{ color: ui.muted, fontSize: 12, fontWeight: 400 }}> old admin flag</span>}
              </Cell>
              <Cell label="Role">
                <select
                  aria-label={`Role for ${u.email}`}
                  style={selectStyle}
                  value={u.role || ''}
                  disabled={!manageable || busy !== null}
                  onChange={(e) => changeRole(u, e.target.value)}
                >
                  <option value="">No access</option>
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value} disabled={!grantable.some((g) => g.value === r.value)}>
                      {r.label}
                    </option>
                  ))}
                </select>
                {u.role === 'client' && (
                  <div style={{ fontSize: 12, color: ui.muted, marginTop: 4 }}>
                    {(u.clients || []).join(', ') || 'no names set'}
                    {u.clientMasters ? ' · masters' : ''}
                    <div>
                      {manageable && (
                        <button
                          type="button"
                          onClick={() => editClientAccess(u)}
                          disabled={busy !== null}
                          style={{ border: 'none', background: 'none', padding: 0, color: ui.link, cursor: 'pointer', fontSize: 12 }}
                        >
                          Edit names
                        </button>
                      )}
                      {' · '}
                      <a href={previewHref(u.clients || [], !!u.clientMasters)} target="_blank" rel="noopener noreferrer">
                        Preview
                      </a>
                    </div>
                  </div>
                )}
              </Cell>
              <Cell label="Status">{u.disabled ? <Pill tone="grey">Disabled</Pill> : <Pill tone="green">Active</Pill>}</Cell>
              <Cell label="Last sign-in" muted>
                {when(u.lastSignInAt)}
              </Cell>
              <Cell label="Actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <SmallButton onClick={() => toggleDisabled(u)} disabled={!manageable || busy !== null}>
                  {u.disabled ? 'Enable' : 'Disable'}
                </SmallButton>
                <SmallButton onClick={() => resetLink(u)} disabled={!manageable || busy !== null || !u.email}>
                  Reset link
                </SmallButton>
                {u.disabled && (
                  <SmallButton onClick={() => deleteUser(u)} disabled={!manageable || busy !== null} tone="danger">
                    Delete
                  </SmallButton>
                )}
              </Cell>
            </TRow>
          );
        })}
      </Panel>
      <p style={{ margin: '10px 0 0', fontSize: 13, color: ui.muted }}>
        Disabling an account signs it out and blocks sign-in; nothing is deleted. Roles take effect at the person's next sign-in, or
        straight away when lowered.
        {orphans > 0 && ` ${orphans} profile record(s) have no sign-in account behind them (left over from removed accounts).`}
      </p>
    </section>
  );
};

export default UsersManager;
